import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import {
  ArrowRight,
  Check,
  FolderOpen,
  HelpCircle,
  Inbox,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { type ChangeEvent, type ReactNode, useState } from "react";
import { z } from "zod";
import {
  Avatar,
  Badge,
  BreakdownBarList,
  BreakdownDonutChart,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ChartEmptyState,
  Checkbox,
  Combobox,
  type ComboboxOption,
  CopyPlate,
  DatePicker,
  DateRangePicker,
  type DateRangeSelection,
  Dialog,
  DropdownMenu,
  DropdownMenuChevron,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  FileInput,
  FileDropzone,
  DescriptionList,
  DetailLayout,
  FilterField,
  FilterToggle,
  Image,
  Input,
  LicensePlate,
  LicensePlateInput,
  Lightbox,
  MultiSelect,
  Pagination,
  Progress,
  RadioGroup,
  type RadioGroupOption,
  resolvePresetRange,
  SearchInput,
  Segmented,
  type SegmentedOption,
  Separator,
  Sheet,
  Skeleton,
  SortableColumnHeader,
  StatCard,
  StatusDot,
  Switch,
  TBody,
  THead,
  Table,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Td,
  Textarea,
  Th,
  Tr,
  TrendBarChart,
  Tooltip,
  VehicleImage,
  useToast,
} from "@rumbledirect/ui-v2";
import { Field } from "@/components/ui/Field";
import { AnimatedNumber, Reveal } from "@rumbledirect/ui-v2/motion";
import { SurveyForm } from "@/survey";

export const Route = createFileRoute("/_authenticated/showcase")({
  component: ShowcasePage,
});

const BUTTON_VARIANTS = [
  "primary",
  "secondary",
  "outline",
  "ghost",
  "subtle",
  "destructive",
] as const;

const BADGE_VARIANTS = [
  "neutral",
  "primary",
  "success",
  "warning",
  "danger",
  "info",
  "progress",
  "done",
  "outline",
] as const;

const STATUS_TONES = [
  "neutral",
  "primary",
  "success",
  "warning",
  "danger",
  "info",
  "progress",
  "done",
] as const;

const TABLE_ROWS = [
  {
    dossier: "24-091",
    vehicle: "Renault Clio",
    status: "In behandeling",
    tone: "progress",
  },
  {
    dossier: "24-088",
    vehicle: "Volkswagen Polo",
    status: "Afgerond",
    tone: "done",
  },
  { dossier: "24-072", vehicle: "Peugeot 208", status: "Nieuw", tone: "info" },
] as const;

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Reveal>
      <section className="space-y-4">
        <div className="space-y-1">
          <h2 className="font-display text-xl font-semibold tracking-tight">
            {title}
          </h2>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {children}
      </section>
    </Reveal>
  );
}

function StatCardDemo() {
  const [selected, setSelected] = useState<"total" | "unread">("total");

  return (
    <div className="grid max-w-md gap-3 sm:grid-cols-2">
      <StatCard
        label="Totaal"
        icon={FolderOpen}
        value={<AnimatedNumber value={128} />}
        onClick={() => setSelected("total")}
        selected={selected === "total"}
      />
      <StatCard
        label="Ongelezen"
        icon={Inbox}
        value={<AnimatedNumber value={12} />}
        onClick={() => setSelected("unread")}
        selected={selected === "unread"}
      />
    </div>
  );
}

function FilterToggleDemo() {
  const [unread, setUnread] = useState(true);
  const [archived, setArchived] = useState(false);

  return (
    <div className="flex flex-wrap gap-2">
      <FilterToggle active={unread} onClick={() => setUnread((v) => !v)}>
        <StatusDot tone="info" />
        Ongelezen
      </FilterToggle>
      <FilterToggle active={archived} onClick={() => setArchived((v) => !v)}>
        <StatusDot tone="neutral" />
        Gearchiveerd
      </FilterToggle>
    </div>
  );
}

function FilterFieldDemo() {
  const [site, setSite] = useState<string | undefined>("site-1");
  const [from, setFrom] = useState("2026-08-10");

  return (
    <div className="flex flex-wrap items-end gap-3">
      <FilterField label="Vanaf">
        <DatePicker aria-label="Vanaf" value={from} onValueChange={setFrom} />
      </FilterField>
      <FilterField label="Locatie">
        <Combobox
          aria-label="Locatie"
          value={site}
          onValueChange={setSite}
          options={[
            { value: "site-1", label: "Amsterdam" },
            { value: "site-2", label: "Zwolle" },
          ]}
          placeholder="Alle locaties"
        />
      </FilterField>
      <FilterField label="Zoeken" className="grow">
        <Input aria-label="Zoeken" placeholder="Zoek op naam" />
      </FilterField>
    </div>
  );
}

function DateRangePickerDemo() {
  const anchor = new Date(2026, 8, 9);
  const [value, setValue] = useState<DateRangeSelection>({
    preset: "last30Days",
    range: resolvePresetRange("last30Days", anchor),
    compare: false,
    comparisonMode: "previousPeriod",
  });

  return (
    <DateRangePicker
      aria-label="Periode"
      anchor={anchor}
      value={value}
      onValueChange={setValue}
    />
  );
}

const CHART_DAILY_TREND = Array.from({ length: 21 }, (_, index) => ({
  label: `2026-08-${String(index + 1).padStart(2, "0")}`,
  value: Math.round(4 + Math.sin(index / 2) * 3 + (index % 5)),
}));

const CHART_DOSSIER_TYPES = [
  { label: "Schade", value: 62 },
  { label: "Glas", value: 24 },
  { label: "Onderhoud", value: 9 },
  { label: "Niet vastgelegd", value: 5, muted: true },
];

const CHART_BRANDS = [
  { label: "Volkswagen", value: 38 },
  { label: "Renault", value: 27 },
  { label: "Audi", value: 19 },
  { label: "Peugeot", value: 12 },
  { label: "Toyota", value: 8 },
];

/**
 * A representative slice of the tenant-configurable intake schema (panels +
 * the four question types admin-v2 renders), mounted through the same
 * `SurveyForm` the dossier-create flow will use. Lives here so the survey
 * renderers are demoed like every other primitive — they are only reachable
 * through a SurveyJS model, so a schema is the honest demo.
 */
const SURVEY_SHOWCASE_SCHEMA = {
  elements: [
    {
      type: "panel",
      name: "intake",
      title: "1. Gegevens",
      elements: [
        {
          type: "text",
          name: "firstName",
          title: "Voornaam",
          isRequired: true,
        },
        {
          type: "licensePlate",
          name: "licensePlate",
          title: "Kenteken voertuig",
          payloadPath: "licensePlate",
          isRequired: true,
        },
        {
          type: "datepicker",
          name: "damageAt",
          title: "Schadedatum",
          payloadPath: "damageAt",
          isRequired: true,
        },
        {
          type: "dropdown",
          name: "driverType",
          title: "Type bestuurder",
          isRequired: true,
          choices: [
            { value: "lease", text: "Lease" },
            { value: "private", text: "Particulier" },
          ],
        },
        {
          type: "checkbox",
          name: "channels",
          title: "Contactkanalen",
          description: "Waar mogen we je op bereiken?",
          choices: [
            { value: "email", text: "E-mail" },
            { value: "phone", text: "Telefoon" },
          ],
        },
        {
          type: "text",
          name: "leaseCompany",
          title: "Leasemaatschappij",
          visibleIf: "{driverType} = 'lease'",
          startWithNewLine: false,
        },
        {
          type: "comment",
          name: "remarks",
          title: "Opmerkingen",
          description: "Alles wat we verder moeten weten.",
          rows: 3,
        },
      ],
    },
  ],
} as const;

function SurveyDemo() {
  return (
    <div className="max-w-2xl">
      <SurveyForm schema={SURVEY_SHOWCASE_SCHEMA} />
    </div>
  );
}

function ChartsDemo() {
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Ontvangen schadefoto's</CardTitle>
          <CardDescription>Per dag in de geselecteerde periode</CardDescription>
        </CardHeader>
        <CardContent>
          <TrendBarChart
            aria-label="Schadefoto's per dag"
            data={CHART_DAILY_TREND}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Dossiertypen</CardTitle>
        </CardHeader>
        <CardContent>
          <BreakdownDonutChart
            aria-label="Dossiertypen"
            data={CHART_DOSSIER_TYPES}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Herkomst dossiers</CardTitle>
        </CardHeader>
        <CardContent>
          <BreakdownBarList data={CHART_DOSSIER_TYPES} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Meest voorkomende merken</CardTitle>
        </CardHeader>
        <CardContent>
          <BreakdownBarList data={CHART_BRANDS} variant="ranked" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Lege periode</CardTitle>
          <CardDescription>
            ChartEmptyState, wat elke grafiek toont zonder gegevens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartEmptyState />
        </CardContent>
      </Card>
    </div>
  );
}

function PaginationDemo() {
  const [page, setPage] = useState(2);

  return (
    <Pagination
      page={page}
      pageSize={15}
      totalItems={52}
      onPageChange={setPage}
    />
  );
}

/**
 * Two independent buttons (not a real toggling sort) so all three states —
 * inactive, active ascending, active descending — are visible side by side
 * rather than only reachable by clicking through them.
 */
function SortableColumnHeaderDemo() {
  const [active, setActive] = useState<"kenteken" | "aangemaakt">("aangemaakt");
  const [direction, setDirection] = useState<"asc" | "desc">("asc");

  const toggle = (column: "kenteken" | "aangemaakt") => {
    if (active === column) {
      setDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setActive(column);
      setDirection("asc");
    }
  };

  return (
    <Table>
      <THead>
        <Tr>
          <Th
            aria-sort={
              active === "kenteken"
                ? direction === "asc"
                  ? "ascending"
                  : "descending"
                : undefined
            }
          >
            <SortableColumnHeader
              direction={active === "kenteken" ? direction : null}
              accessibleLabel={
                active === "kenteken" && direction === "asc"
                  ? "Sorteer op Kenteken aflopend"
                  : "Sorteer op Kenteken oplopend"
              }
              onSort={() => toggle("kenteken")}
            >
              Kenteken
            </SortableColumnHeader>
          </Th>
          <Th
            aria-sort={
              active === "aangemaakt"
                ? direction === "asc"
                  ? "ascending"
                  : "descending"
                : undefined
            }
          >
            <SortableColumnHeader
              direction={active === "aangemaakt" ? direction : null}
              accessibleLabel={
                active === "aangemaakt" && direction === "asc"
                  ? "Sorteer op Aangemaakt aflopend"
                  : "Sorteer op Aangemaakt oplopend"
              }
              onSort={() => toggle("aangemaakt")}
            >
              Aangemaakt
            </SortableColumnHeader>
          </Th>
          <Th>Status</Th>
        </Tr>
      </THead>
    </Table>
  );
}

function DialogDemo() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)}>Dialog openen</Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Dossier archiveren"
        description="Weet je zeker dat je dit dossier wilt archiveren?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={() => setOpen(false)}>Archiveren</Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Het dossier verdwijnt uit het actieve overzicht, maar blijft
          doorzoekbaar in het archief.
        </p>
      </Dialog>
    </>
  );
}

function SheetDemo() {
  const [open, setOpen] = useState(false);
  const [compactOpen, setCompactOpen] = useState(false);

  return (
    <div className="flex flex-wrap gap-3">
      <Button onClick={() => setOpen(true)}>Sheet openen</Button>
      <Button variant="outline" onClick={() => setCompactOpen(true)}>
        Sheet met icon-header
      </Button>
      <Sheet
        open={open}
        onOpenChange={setOpen}
        title="Dossier 24-091"
        description="Renault Clio - Sanne de Vries"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={() => setOpen(false)}>Opslaan</Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Detailweergave met de volledige dossierhistorie, notities en bijlagen.
        </p>
      </Sheet>
      <Sheet
        open={compactOpen}
        onOpenChange={setCompactOpen}
        icon={<Check aria-hidden="true" className="size-4" />}
        title="Taakdetails"
        description="Compacte header: badge, korte titel en een uitleg van een regel."
        footer={
          <Button variant="ghost" onClick={() => setCompactOpen(false)}>
            Sluiten
          </Button>
        }
      >
        <p className="text-sm text-muted-foreground">
          Gebruik deze variant als de titel generiek is en het onderwerp in de
          body zelf staat.
        </p>
      </Sheet>
    </div>
  );
}

function EmptyStateDemo() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="rounded-2xl border border-border/70 bg-surface shadow-xs">
        <EmptyState
          icon={FolderOpen}
          title="Nog geen dossiers"
          description="Dossiers verschijnen hier zodra ze zijn aangemaakt."
        />
      </div>
      <div className="rounded-2xl border border-border/70 bg-surface shadow-xs">
        <EmptyState
          icon={Inbox}
          title="Nog geen berichten"
          description="Inkomende berichten verschijnen hier."
          action={
            <Button variant="outline" size="sm">
              <Plus className="size-4" aria-hidden="true" />
              Bericht toevoegen
            </Button>
          }
        />
      </div>
    </div>
  );
}

function ToastDemo() {
  const toast = useToast();

  return (
    <div className="flex flex-wrap gap-3">
      <Button onClick={() => toast.success("Dossier opgeslagen.")}>
        Success-toast
      </Button>
      <Button
        variant="outline"
        onClick={() => toast.error("Opslaan mislukt. Probeer het opnieuw.")}
      >
        Error-toast
      </Button>
    </div>
  );
}

const SEGMENTED_STATUS_OPTIONS: SegmentedOption<
  "all" | "needs_review" | "processed" | "ignored"
>[] = [
  { value: "all", label: "Alle" },
  { value: "needs_review", label: "Controle" },
  { value: "processed", label: "Verwerkt" },
  { value: "ignored", label: "Genegeerd" },
];

const RADIO_ANSWER_OPTIONS: readonly RadioGroupOption[] = [
  { value: "yes", label: "Ja" },
  { value: "no", label: "Nee" },
  { value: "unknown", label: "Onbekend" },
];

const RADIO_CONTACT_OPTIONS: readonly RadioGroupOption[] = [
  { value: "email", label: "E-mail" },
  { value: "phone", label: "Telefoon" },
  { value: "post", label: "Post (niet beschikbaar)", disabled: true },
];

const RADIO_DISABLED_OPTIONS = RADIO_ANSWER_OPTIONS.slice(0, 2);

function RadioGroupDemo(): ReactNode {
  const [answer, setAnswer] = useState<string>();
  const [contact, setContact] = useState<string>();
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <div>
        <RadioGroup
          legend="Is het voertuig rijdbaar?"
          orientation="responsive"
          value={answer}
          onValueChange={setAnswer}
          options={RADIO_ANSWER_OPTIONS}
        />
        <Button
          variant="ghost"
          size="sm"
          disabled={answer === undefined}
          onClick={() => setAnswer(undefined)}
        >
          Antwoord wissen
        </Button>
      </div>
      <div>
        <RadioGroup
          legend="Hoe wilt u contact?"
          value={contact}
          onValueChange={setContact}
          required
          aria-invalid={contact === undefined}
          aria-describedby={
            contact === undefined ? "radio-demo-error" : undefined
          }
          options={RADIO_CONTACT_OPTIONS}
        />
        {contact === undefined && (
          <p id="radio-demo-error" className="text-xs text-destructive">
            Kies een contactmethode.
          </p>
        )}
      </div>
      <RadioGroup
        legend="Horizontale keuze"
        orientation="horizontal"
        value="no"
        onValueChange={() => undefined}
        disabled
        options={RADIO_DISABLED_OPTIONS}
      />
    </div>
  );
}

function SegmentedDemo() {
  const [status, setStatus] = useState<
    "all" | "needs_review" | "processed" | "ignored"
  >("needs_review");
  const [size, setSize] = useState<"sm" | "md">("md");

  return (
    <div className="space-y-4">
      <Segmented
        aria-label="Status"
        value={status}
        onValueChange={setStatus}
        options={SEGMENTED_STATUS_OPTIONS}
      />
      <Segmented
        aria-label="Grootte"
        value={size}
        onValueChange={setSize}
        size="sm"
        options={[
          { value: "sm", label: "Klein" },
          { value: "md", label: "Normaal" },
        ]}
      />
      <Segmented
        aria-label="Weergave (volledige breedte)"
        value={status}
        onValueChange={setStatus}
        fluid
        className="max-w-md"
        options={SEGMENTED_STATUS_OPTIONS}
      />
    </div>
  );
}

function TabsDemo() {
  const [section, setSection] = useState("overview");
  const [underlineSection, setUnderlineSection] = useState("overview");

  return (
    <div className="space-y-8">
      <Tabs value={section} onValueChange={setSection} className="max-w-2xl">
        <TabsList aria-label="Dossiersecties">
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="messages">Berichten</TabsTrigger>
          <TabsTrigger value="documents">Documenten</TabsTrigger>
          <TabsTrigger value="planning" disabled>
            Planning
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="overview"
          className="mt-4 rounded-xl border border-border bg-card p-4 text-sm"
        >
          De belangrijkste dossiergegevens staan in dit paneel.
        </TabsContent>
        <TabsContent
          value="messages"
          className="mt-4 rounded-xl border border-border bg-card p-4 text-sm"
        >
          Berichten blijven gekoppeld aan het actieve dossier.
        </TabsContent>
        <TabsContent
          value="documents"
          className="mt-4 rounded-xl border border-border bg-card p-4 text-sm"
        >
          Documenten worden pas gemount wanneer dit tabblad actief is.
        </TabsContent>
        <TabsContent value="planning" />
      </Tabs>

      <Tabs
        variant="underline"
        value={underlineSection}
        onValueChange={setUnderlineSection}
      >
        <TabsList aria-label="Rapportagetabs">
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="intake">Intake</TabsTrigger>
          <TabsTrigger value="repair">Herstel</TabsTrigger>
        </TabsList>
        <TabsContent
          value="overview"
          className="mt-4 rounded-xl border border-border bg-card p-4 text-sm"
        >
          De underline-variant verdeelt de tabs over de volle breedte.
        </TabsContent>
        <TabsContent
          value="intake"
          className="mt-4 rounded-xl border border-border bg-card p-4 text-sm"
        >
          Een stille scheidingslijn loopt onder de hele strip.
        </TabsContent>
        <TabsContent
          value="repair"
          className="mt-4 rounded-xl border border-border bg-card p-4 text-sm"
        >
          De actieve regel schuift daar overheen mee naar de actieve tab.
        </TabsContent>
      </Tabs>
    </div>
  );
}
const SHOWCASE_PHOTOS = [
  {
    src: "https://picsum.photos/id/111/800/600",
    alt: "Vooraanzicht voertuig met schade aan de bumper",
  },
  {
    src: "https://picsum.photos/id/183/800/600",
    alt: "Zijaanzicht linksvoor",
  },
  {
    src: "https://picsum.photos/id/1071/800/600",
    alt: "Detailopname lakschade portier",
  },
  {
    src: "https://picsum.photos/id/1072/800/600",
    alt: "Achteraanzicht voertuig",
  },
] as const;

function ImageDemo() {
  return (
    <div className="grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
      {SHOWCASE_PHOTOS.map((photo) => (
        <Image key={photo.src} src={photo.src} alt={photo.alt} />
      ))}
      {/* Fallback state: missing source renders the placeholder. */}
      <Image src={null} alt="Geen foto beschikbaar" />
    </div>
  );
}

function LightboxDemo() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  return (
    <>
      <div className="flex max-w-2xl flex-wrap gap-3">
        {SHOWCASE_PHOTOS.map((photo, i) => (
          <button
            key={photo.src}
            type="button"
            onClick={() => {
              setIndex(i);
              setOpen(true);
            }}
            className="w-28 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Open ${photo.alt} in het vergrootglas`}
          >
            <Image src={photo.src} alt={photo.alt} ratio="1" />
          </button>
        ))}
      </div>
      <Lightbox
        open={open}
        onOpenChange={setOpen}
        items={SHOWCASE_PHOTOS}
        index={index}
        onIndexChange={setIndex}
      />
    </>
  );
}

function DropdownMenuDemo() {
  const toast = useToast();

  return (
    <div className="flex flex-wrap items-center gap-6">
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button variant="outline" size="sm">
            Acties
            <DropdownMenuChevron />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => toast.success("Bewerken gekozen")}>
            <Pencil />
            Bewerken
          </DropdownMenuItem>
          <DropdownMenuItem
            tone="destructive"
            onSelect={() => toast.success("Verwijderen gekozen")}
          >
            <Trash2 />
            Verwijderen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger>
          <button
            type="button"
            aria-label="Meer acties voor dit dossier"
            className="grid size-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <MoreHorizontal className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => toast.success("Dossier geopend")}>
            <FolderOpen />
            Dossier openen
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <Check />
            Afronden (niet beschikbaar)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const COMBOBOX_SERVICE_PARTNER_OPTIONS: ComboboxOption[] = [
  { value: "amsterdam", label: "Amsterdam", hint: "AMS" },
  { value: "rotterdam", label: "Rotterdam", hint: "RTM" },
  { value: "den-haag", label: "Den Haag", hint: "DH" },
  { value: "utrecht", label: "Utrecht", hint: "UTR" },
  { value: "eindhoven", label: "Eindhoven", hint: "EHV" },
  { value: "groningen", label: "Groningen", hint: "GRQ" },
  { value: "tilburg", label: "Tilburg", hint: "TIL" },
  { value: "almere", label: "Almere", hint: "ALM" },
  { value: "breda", label: "Breda", hint: "BRD" },
  { value: "nijmegen", label: "Nijmegen", hint: "NIJ" },
  { value: "arnhem", label: "Arnhem", hint: "ARN" },
  { value: "haarlem", label: "Haarlem", hint: "HRL" },
];

function ComboboxDemo() {
  const [servicePartner, setServicePartner] = useState<string | undefined>(
    "rotterdam",
  );
  const [ghostPartner, setGhostPartner] = useState<string | undefined>();
  const [iconOnlyPartner, setIconOnlyPartner] = useState<string | undefined>(
    "utrecht",
  );

  return (
    <div className="flex flex-wrap items-center gap-4">
      <Combobox
        aria-label="Vestiging"
        placeholder="Alle vestigingen"
        searchPlaceholder="Zoek een vestiging..."
        value={servicePartner}
        onValueChange={setServicePartner}
        options={COMBOBOX_SERVICE_PARTNER_OPTIONS}
      />
      <Combobox
        aria-label="Vestiging (ghost)"
        variant="ghost"
        placeholder="Ghost variant"
        searchPlaceholder="Zoek een vestiging..."
        value={ghostPartner}
        onValueChange={setGhostPartner}
        options={COMBOBOX_SERVICE_PARTNER_OPTIONS}
      />
      <Combobox
        aria-label="Vestiging (alleen icoon)"
        variant="ghost"
        size="sm"
        iconOnly
        icon={
          <Avatar
            name={
              COMBOBOX_SERVICE_PARTNER_OPTIONS.find(
                (option) => option.value === iconOnlyPartner,
              )?.label
            }
            size="sm"
          />
        }
        searchPlaceholder="Zoek een vestiging..."
        value={iconOnlyPartner}
        onValueChange={setIconOnlyPartner}
        options={COMBOBOX_SERVICE_PARTNER_OPTIONS}
      />
    </div>
  );
}

const MULTISELECT_CATEGORY_OPTIONS: ComboboxOption[] = [
  { value: "a", label: "Categorie A" },
  { value: "b", label: "Categorie B" },
  { value: "c", label: "Categorie C" },
  { value: "shared", label: "Deelmobiliteit" },
  { value: "charging", label: "Laadpas" },
];

function MultiSelectDemo() {
  const [categories, setCategories] = useState<string[]>(["a", "c"]);

  return (
    <MultiSelect
      aria-label="Mobiliteitscategorieën"
      placeholder="Alle categorieën"
      searchPlaceholder="Zoek een categorie..."
      values={categories}
      onValuesChange={setCategories}
      options={MULTISELECT_CATEGORY_OPTIONS}
      fluid
      className="max-w-md"
    />
  );
}

function CheckboxDemo() {
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(true);

  return (
    <Checkbox
      aria-label="Deels geselecteerd"
      checked={checked}
      indeterminate={indeterminate}
      onChange={(event) => {
        setChecked(event.currentTarget.checked);
        setIndeterminate(false);
      }}
    />
  );
}

function SwitchDemo() {
  const [active, setActive] = useState(true);
  const [notify, setNotify] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <span className="flex items-center gap-3 text-sm font-medium">
        <Switch
          aria-label="Actief"
          checked={active}
          onCheckedChange={setActive}
        />
        Actief
      </span>
      <span className="flex items-center gap-3 text-sm font-medium">
        <Switch
          aria-label="Meldingen (klein)"
          checked={notify}
          onCheckedChange={setNotify}
          size="sm"
        />
        Meldingen (klein)
      </span>
      <span className="flex items-center gap-3 text-sm font-medium text-muted-foreground">
        <Switch
          aria-label="Uitgeschakeld"
          checked
          disabled
          onCheckedChange={() => undefined}
        />
        Uitgeschakeld
      </span>
    </div>
  );
}

function TooltipDemo() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-8">
        <Tooltip label="Boven" side="top">
          <Button variant="outline" size="sm">
            Top
          </Button>
        </Tooltip>
        <Tooltip label="Onder" side="bottom">
          <Button variant="outline" size="sm">
            Bottom
          </Button>
        </Tooltip>
        <Tooltip label="Links" side="left">
          <Button variant="outline" size="sm">
            Left
          </Button>
        </Tooltip>
        <Tooltip label="Rechts" side="right">
          <Button variant="outline" size="sm">
            Right
          </Button>
        </Tooltip>
      </div>
      <div className="flex flex-wrap items-center gap-8">
        <span className="flex items-center gap-1.5 text-sm">
          Interactieve trigger
          <Tooltip label="Extra uitleg bij dit veld.">
            <button
              type="button"
              aria-label="Meer informatie"
              className="grid size-5 place-items-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
            >
              <HelpCircle className="size-4" aria-hidden="true" />
            </button>
          </Tooltip>
        </span>
        <Tooltip
          label="Niet-interactieve trigger, bereikbaar via tabIndex"
          tabIndex={0}
          className="text-sm text-muted-foreground underline decoration-dotted"
        >
          Niet-interactieve trigger
        </Tooltip>
      </div>
    </div>
  );
}

function DatePickerDemo() {
  const [date, setDate] = useState("");
  const [compactDate, setCompactDate] = useState("");

  return (
    <div className="grid max-w-md grid-cols-2 gap-3">
      <div>
        <span className="text-xs text-muted-foreground">Standaard</span>
        <DatePicker
          aria-label="Schadedatum"
          placeholder="Kies een datum"
          value={date}
          onValueChange={setDate}
          className="mt-1"
        />
      </div>
      <div>
        <span className="text-xs text-muted-foreground">Compact</span>
        <DatePicker
          compact
          aria-label="Schadedatum compact"
          placeholder="Kies een datum"
          value={compactDate}
          onValueChange={setCompactDate}
          className="mt-1"
        />
      </div>
    </div>
  );
}

function InputTextareaDemo() {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");

  return (
    <div className="flex max-w-md flex-col gap-3">
      <Input
        aria-label="Naam"
        placeholder="Voornaam"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Textarea
        aria-label="Bericht"
        placeholder="Typ een bericht..."
        value={message}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          setMessage(event.target.value)
        }
      />
    </div>
  );
}

function SearchInputDemo() {
  const [term, setTerm] = useState("");

  return (
    <div className="max-w-md">
      <SearchInput
        aria-label="Zoeken"
        placeholder="Zoek op kenteken, bestuurder of voertuig…"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        onClear={() => setTerm("")}
      />
    </div>
  );
}

const fieldDemoSchema = z.object({
  email: z.string().email("Vul een geldig e-mailadres in."),
});

/**
 * Minimal TanStack Form + zod form proving the `Field` wrapper's contract:
 * pass `<form.Field name>`'s callback `field` into `Field`, which renders the
 * label, wires `aria-invalid`/`aria-describedby`, and shows the zod error
 * once the field has been touched.
 */
function FieldDemo() {
  const form = useForm({
    defaultValues: { email: "" },
    validators: { onChange: fieldDemoSchema },
  });

  return (
    <form.Field name="email">
      {(field) => (
        <Field
          field={field}
          label="E-mailadres"
          description="Gebruikt voor de bevestigingsmail."
          required
          className="max-w-sm"
        >
          {(controlProps) => (
            <Input
              {...controlProps}
              type="email"
              placeholder="naam@voorbeeld.nl"
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
            />
          )}
        </Field>
      )}
    </form.Field>
  );
}

function LicensePlateInputDemo() {
  const [plate, setPlate] = useState("");

  return (
    <LicensePlateInput
      aria-label="Kenteken"
      placeholder="XX-99-XX"
      value={plate}
      onChange={(event) => setPlate(event.target.value)}
      actionSlot={
        <button
          type="button"
          aria-label="Voertuig ophalen"
          className="grid size-7 place-items-center rounded-md text-[#15171c] transition hover:bg-[#15171c]/10"
        >
          <Search className="size-4" aria-hidden="true" />
        </button>
      }
    />
  );
}

function FileInputDemo() {
  const [uploading, setUploading] = useState(false);

  return (
    <div className="grid max-w-3xl gap-6 sm:grid-cols-2">
      <FileInput
        label="Bestand"
        maxSizeHint="PDF, JPG, PNG, WEBP of EML · max. 20 MB"
        onFileSelected={() => {
          // Fake an upload so the loading state is reachable from the showcase.
          setUploading(true);
          setTimeout(() => setUploading(false), 1500);
        }}
        loading={uploading}
      />
      <FileInput
        label="Alleen afbeeldingen"
        accept="image/jpeg,image/png,image/webp"
        onFileSelected={() => undefined}
      />
      <FileInput
        label="Ongeldig bestand"
        aria-invalid
        maxSizeHint="Dit bestandstype wordt niet ondersteund."
        onFileSelected={() => undefined}
      />
      <FileInput
        label="Uitgeschakeld"
        disabled
        onFileSelected={() => undefined}
      />
    </div>
  );
}

function ShowcasePage() {
  return (
    <div className="mx-auto max-w-5xl space-y-12">
      <Reveal>
        <header className="space-y-1">
          <h1 className="font-display text-3xl font-bold tracking-tight">
            Admin v2 design system
          </h1>
          <p className="text-muted-foreground">
            Core primitives, tokens and motion for the new admin surface.
          </p>
        </header>
      </Reveal>

      <Section title="Buttons" description="Variants and sizes.">
        <div className="flex flex-wrap gap-3">
          {BUTTON_VARIANTS.map((variant) => (
            <Button key={variant} variant={variant}>
              {variant}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm">
            <Plus />
            Small
          </Button>
          <Button size="md">
            Medium
            <ArrowRight />
          </Button>
          <Button size="lg" variant="subtle">
            <Sparkles />
            Large
          </Button>
          <Button size="icon" variant="outline" aria-label="Confirm">
            <Check />
          </Button>
          <Button size="icon-sm" variant="destructive" aria-label="Delete">
            <Trash2 />
          </Button>
          <Button disabled>Disabled</Button>
        </div>
      </Section>

      <Section
        title="Badges"
        description="Tonal labels, including the progress and done accent tones."
      >
        <div className="flex flex-wrap gap-2">
          {BADGE_VARIANTS.map((variant) => (
            <Badge key={variant} variant={variant}>
              {variant}
            </Badge>
          ))}
        </div>
      </Section>

      <Section
        title="Status dots"
        description="Small indicators, optionally pulsing."
      >
        <div className="flex flex-wrap gap-4">
          {STATUS_TONES.map((tone) => (
            <span key={tone} className="inline-flex items-center gap-2 text-sm">
              <StatusDot tone={tone} pulse={tone === "info"} />
              {tone}
            </span>
          ))}
        </div>
      </Section>

      <Section
        title="Stat card"
        description="Summary tile with a label, value and icon. Passing onClick turns the tile into a one-tap filter that reports its state via aria-pressed."
      >
        <div className="grid max-w-md gap-3 sm:grid-cols-2">
          <StatCard label="Totaal" value={128} icon={FolderOpen} />
          <StatCard label="Open" value={12} icon={FolderOpen} />
        </div>
        <StatCardDemo />
      </Section>

      <Section
        title="Filter toggle"
        description="One-tap pill filters for list views; state is exposed via aria-pressed."
      >
        <FilterToggleDemo />
      </Section>

      <Section
        title="Radio group"
        description="Native single-answer form control with an unanswered initial state, keyboard navigation, validation and disabled options. Labels have 44px touch targets. Responsive short choices stay on one line when the group has room and stack together in narrow columns."
      >
        <RadioGroupDemo />
      </Section>

      <Section
        title="Segmented"
        description="Accessible radiogroup with a measured sliding pill; arrow keys, Home/End move the selection."
      >
        <SegmentedDemo />
      </Section>

      <Section
        title="Tabs"
        description="Radix-backed panel navigation with URL-friendly controlled state, keyboard navigation and a measured sliding indicator."
      >
        <TabsDemo />
      </Section>

      <Section
        title="Dropdown menu"
        description="Contextmenu bij een rij of knop, met een destructieve variant voor onomkeerbare acties. Bediening met muis en toetsenbord; het menu wordt geportald, dus het wordt nooit afgekapt door een omliggende kaart."
      >
        <DropdownMenuDemo />
      </Section>

      <Section
        title="Combobox"
        description="Searchable single-select. Focus stays on the search input; options are addressed via aria-activedescendant."
      >
        <ComboboxDemo />
      </Section>

      <Section
        title="Multi-select"
        description="Searchable multi-select listbox (aria-multiselectable). Picks stay open for several selections and render as removable chips; Backspace on an empty query drops the last chip."
      >
        <MultiSelectDemo />
      </Section>

      <Section title="Checkbox">
        <div className="flex flex-wrap items-center gap-6">
          <Checkbox aria-label="Niet geselecteerd" />
          <Checkbox aria-label="Geselecteerd" defaultChecked />
          <CheckboxDemo />
          <Checkbox aria-label="Uitgeschakeld, niet geselecteerd" disabled />
          <Checkbox
            aria-label="Uitgeschakeld, geselecteerd"
            defaultChecked
            disabled
          />
          <Checkbox
            aria-label="Uitgeschakeld, deels geselecteerd"
            indeterminate
            disabled
          />
          <label
            htmlFor="showcase-checkbox-label"
            className="inline-flex items-center gap-2 text-sm font-medium"
          >
            <Checkbox id="showcase-checkbox-label" />
            Selecteer deze rij
          </label>
        </div>
      </Section>

      <Section
        title="Switch"
        description="Controlled boolean toggle rendered as a WAI-ARIA switch, with sm/md sizes and a disabled state."
      >
        <SwitchDemo />
      </Section>

      <Section
        title="Tooltip"
        description="Hover/focus bubble portaled to document.body via floating-ui, so it can escape an overflow-hidden ancestor. The bubble is decorative (aria-hidden); a trigger that needs the explanation to reach assistive tech wires its own aria-describedby instead."
      >
        <TooltipDemo />
      </Section>

      <Section
        title="DatePicker"
        description="Single-date picker on a Radix Popover with a Dutch, Monday-first month grid. Values are ISO date-only strings; supports min/max range and clear."
      >
        <DatePickerDemo />
      </Section>

      <Section
        title="DateRangePicker"
        description="Report-period picker: presets, a custom start/end pair, and an optional comparison period. The selection is always fully resolved, so a view-configuration-driven report reads its ranges straight off it."
      >
        <DateRangePickerDemo />
      </Section>

      <Section
        title="Input & Textarea"
        description="Form-agnostic text primitives: ref-forwarding, controlled/uncontrolled, no built-in label or validation (a future Field wrapper owns those)."
      >
        <InputTextareaDemo />
      </Section>

      <Section
        title="Search input"
        description="Search field with a leading icon and a clear button that appears once there is a value. Used by the dossiers topbar quick-jump."
      >
        <SearchInputDemo />
      </Section>

      <Section
        title="Field"
        description="Label/error wrapper for TanStack Form fields. Pass the render-prop `field` from `<form.Field>` and it wires aria-invalid/aria-describedby onto the composed control."
      >
        <FieldDemo />
      </Section>

      <Section
        title="Filter field"
        description="Caption stacked above a filter control. The caption is aria-hidden, so the control it wraps must carry its own aria-label; that keeps the name from being announced twice."
      >
        <FilterFieldDemo />
      </Section>

      <Section
        title="File input"
        description="Presentational file picker: it reports the chosen file and reflects an in-flight upload, but never uploads by itself. Defaults to the MIME allow-list the API enforces."
      >
        <FileInputDemo />
        <FileDropzone
          label="Audatex calculatie"
          accept=".txt"
          maxSize={10 * 1024 * 1024}
          onFileSelected={() => {}}
        />
        <FileDropzone
          label="Uploaden"
          accept=".txt"
          maxSize={10 * 1024 * 1024}
          loading
          onFileSelected={() => {}}
        />
        <FileDropzone
          label="Uitgeschakeld"
          accept="image/png,image/jpeg,image/webp"
          maxSize={5 * 1024 * 1024}
          disabled
          onFileSelected={() => {}}
        />
        <DescriptionList
          items={[
            { key: "plate", label: "Kenteken", value: "RUMB73" },
            { key: "repairer", label: "Hersteller", value: "Thunder B.V." },
          ]}
        />
      </Section>

      <Section
        title="License plate input"
        description="Editable NL plate, styled to match the read-only LicensePlate primitive. actionSlot is a presentational slot for a later lookup button."
      >
        <LicensePlateInputDemo />
      </Section>

      <Section
        title="Card"
        description="Composable surface with header, content and footer."
      >
        <Card className="max-w-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Dossier 24-091</CardTitle>
              <Badge variant="progress">In behandeling</Badge>
            </div>
            <CardDescription>
              Renault Clio - laatste update 2 uur geleden.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar name="Sanne de Vries" />
              <div className="text-sm">
                <p className="font-medium">Sanne de Vries</p>
                <p className="text-muted-foreground">Toegewezen behandelaar</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Voortgang</span>
                <span className="font-medium">60%</span>
              </div>
              <Progress value={60} aria-label="Dossiervoortgang" />
            </div>
          </CardContent>
          <CardFooter className="justify-end">
            <Button variant="ghost" size="sm">
              Details
            </Button>
            <Button size="sm">
              Openen
              <ArrowRight />
            </Button>
          </CardFooter>
        </Card>
      </Section>

      <Section
        title="Detail layout"
        description="A detail page: the main column with a sticky 20rem rail beside it from lg up. railFirst puts the rail on top below lg; without a rail the page is one column."
      >
        <DetailLayout
          railFirst
          railLabel="Acties"
          rail={
            <Card>
              <CardHeader>
                <CardTitle>Acties</CardTitle>
              </CardHeader>
              <CardContent>
                <Button size="sm" className="w-full">
                  Goedkeuren
                </Button>
              </CardContent>
            </Card>
          }
        >
          <Card>
            <CardHeader>
              <CardTitle>Algemeen</CardTitle>
            </CardHeader>
            <CardContent>
              <DescriptionList
                items={[
                  { key: "plate", label: "Kenteken", value: "RUMB73" },
                  { key: "status", label: "Status", value: "In behandeling" },
                ]}
              />
            </CardContent>
          </Card>
        </DetailLayout>
      </Section>

      <Section
        title="Table"
        description="Composable table primitives for dense, read-only data."
      >
        <Card>
          <Table>
            <THead>
              <Tr>
                <Th>Dossier</Th>
                <Th>Voertuig</Th>
                <Th>Status</Th>
              </Tr>
            </THead>
            <TBody>
              {TABLE_ROWS.map((row) => (
                <Tr key={row.dossier}>
                  <Td className="font-medium">{row.dossier}</Td>
                  <Td className="text-muted-foreground">{row.vehicle}</Td>
                  <Td>
                    <Badge variant={row.tone}>{row.status}</Badge>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          <CardFooter className="justify-between text-sm text-muted-foreground">
            <span>Pagina 1</span>
            <span>3 dossiers</span>
          </CardFooter>
        </Card>
      </Section>

      <Section
        title="Pagination"
        description="List range and previous/next navigation; routes keep ownership of page state and URL synchronization."
      >
        <PaginationDemo />
      </Section>

      <Section
        title="Sortable column header"
        description="Clickable table/columnheader labels: a subtle up/down icon when inactive, a single arrow for the active column and direction. Kenteken is inactive; click Aangemaakt to toggle its direction."
      >
        <SortableColumnHeaderDemo />
      </Section>

      <Section
        title="Charts"
        description="Report chart primitives: a Recharts-based trend/donut chart for the cases axes and arcs earn their keep, plus a plain-CSS breakdown list for proportional/ranked rows."
      >
        <ChartsDemo />
      </Section>

      <Section
        title="Avatars"
        description="Circular avatar with initials fallback."
      >
        <div className="flex items-center gap-4">
          <Avatar name="Sanne de Vries" size="sm" />
          <Avatar name="Youssef El Amrani" size="md" />
          <Avatar name="Rumble Direct" size="lg" />
        </div>
      </Section>

      <Section title="Progress" description="Determinate progress bars.">
        <div className="max-w-md space-y-3">
          <Progress value={25} aria-label="25 procent" />
          <Progress value={60} aria-label="60 procent" />
          <Progress value={92} aria-label="92 procent" />
        </div>
      </Section>

      <Section
        title="Skeleton"
        description="Loading placeholders with a shimmer sweep."
      >
        <Card className="max-w-md">
          <CardContent className="space-y-3 pt-5">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
            <div className="flex items-center gap-3 pt-2">
              <Skeleton className="size-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>

      <Section
        title="Separator"
        description="Horizontal and vertical dividers."
      >
        <div className="max-w-md space-y-4">
          <p className="text-sm text-muted-foreground">Boven de scheiding.</p>
          <Separator />
          <div className="flex h-6 items-center gap-4 text-sm">
            <span>Links</span>
            <Separator orientation="vertical" />
            <span>Midden</span>
            <Separator orientation="vertical" />
            <span>Rechts</span>
          </div>
        </div>
      </Section>
      <Section
        title="License plate"
        description="Dutch (NL) license plate at three sizes."
      >
        <div className="flex flex-wrap items-center gap-4">
          <LicensePlate value="XX-123-Y" size="sm" />
          <LicensePlate value="XX-123-Y" size="md" />
          <LicensePlate value="XX-123-Y" size="lg" />
        </div>
      </Section>

      <Section
        title="Image"
        description="Gekaderde afbeelding met vaste beeldverhouding, shimmer tijdens het laden en een nette fallback wanneer de bron ontbreekt of mislukt."
      >
        <ImageDemo />
      </Section>

      <Section
        title="Lightbox"
        description="Schermvullende afbeeldingsviewer met toetsenbordnavigatie (←/→), gebouwd op hetzelfde Dialog-fundament."
      >
        <LightboxDemo />
      </Section>

      <Section
        title="Dialog"
        description="Gecentreerd, modaal venster voor bevestigingen en korte formulieren."
      >
        <DialogDemo />
      </Section>

      <Section
        title="Sheet"
        description="Zijpaneel voor detailweergaves zonder de context van de achterliggende pagina te verliezen."
      >
        <SheetDemo />
      </Section>

      <Section
        title="EmptyState"
        description="Icoon, titel, omschrijving en optionele actie voor een lijst of paneel zonder inhoud."
      >
        <EmptyStateDemo />
      </Section>
      <Section
        title="Toast"
        description="Kortstondige, zelfsluitende meldingen voor de uitkomst van een actie. Verschijnt rechtsonder en verdwijnt automatisch."
      >
        <ToastDemo />
      </Section>
      <Section
        title="Survey form"
        description="Tenant-configurable SurveyJS-schema's gerenderd met admin-v2 primitives: panel als Card, tekst/commentaar/checkbox/dropdown met het gedeelde label-, marker- en foutenchrome. Kies 'Lease' om de conditionele leasemaatschappij te tonen."
      >
        <SurveyDemo />
      </Section>
      <Section
        title="Copy plate"
        description="A license plate that copies its value to the clipboard on click."
      >
        <div className="flex flex-wrap items-center gap-4">
          <CopyPlate value="XX-123-Y" size="sm" />
          <CopyPlate value="XX-123-Y" size="md" />
          <CopyPlate value="XX-123-Y" size="lg" />
        </div>
      </Section>
      <Section
        title="Vehicle image"
        description="A vehicle marker tinted to its paint colour, with a neutral fallback for unknown colours."
      >
        <div className="flex flex-wrap items-center gap-4">
          <VehicleImage brand="Volkswagen" model="ID.4" color="Parelmoer wit" />
          <VehicleImage brand="Audi" model="A3" color="Zwart metallic" />
          <VehicleImage brand="Renault" model="Clio" color="Blauw" />
          <VehicleImage brand="Onbekend" model="" color={null} />
          <VehicleImage
            brand="Volkswagen"
            model="ID.4"
            color="Parelmoer wit"
            size="lg"
          />
          <VehicleImage
            brand="Volkswagen"
            model="ID.4"
            color="Parelmoer wit"
            src="https://picsum.photos/id/111/320/240"
            srcLabel="Linksvoor"
            size="lg"
          />
        </div>
      </Section>
    </div>
  );
}
